import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Zap, MapPin, Calendar } from "lucide-react";
import { NARRATIVE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsSlider() {
  const [projects, setProjects] = useState([]);
  const [tipo, setTipo] = useState("all"); // "all" | "privato" | "azienda"
  const trackRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const filtered = tipo === "all" ? projects : projects.filter(p => p.tipo === tipo);

  const scroll = (dir) => {
    if (!trackRef.current) return;
    const w = trackRef.current.clientWidth * 0.8;
    trackRef.current.scrollBy({ left: dir * w, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  if (!projects.length) return null;

  return (
    <section id="realizzazioni" data-testid={NARRATIVE.projectSlider} className="bg-[#061414] text-white py-14 md:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline text-emerald-400 mb-3">Configurazioni di esempio</div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter max-w-2xl">
              Ogni progetto parte da esigenze diverse.
            </h2>
            <p className="mt-3 text-sm text-white/75 max-w-xl">
              Esempi illustrativi di configurazioni residenziali e aziendali. Non rappresentano impianti progettati o installati da Bandofotovoltaico24. Potenze, produzione, autoconsumo e convenienza devono essere verificati sul singolo caso.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex border border-white/20 rounded-md">
              {[
                { key: "all", label: "Tutti" },
                { key: "privato", label: "Privati" },
                { key: "azienda", label: "Aziende" },
              ].map((t, i) => (
                <button
                  type="button"
                  aria-pressed={tipo === t.key}
                  key={t.key}
                  onClick={() => setTipo(t.key)}
                  className={`min-h-11 px-3 py-2 text-xs uppercase tracking-wider ${i > 0 ? "border-l border-white/20" : ""} ${tipo === t.key ? "bg-emerald-800 text-white" : "text-white/75 hover:text-white"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button type="button" aria-label="Mostra esempi precedenti" data-testid={NARRATIVE.sliderPrev} onClick={() => scroll(-1)} className="min-h-11 min-w-11 p-2 border border-white/20 rounded-md hover:bg-white/5"><ChevronLeft size={16} /></button>
            <button type="button" aria-label="Mostra esempi successivi" data-testid={NARRATIVE.sliderNext} onClick={() => scroll(1)} className="min-h-11 min-w-11 p-2 border border-white/20 rounded-md hover:bg-white/5"><ChevronRight size={16} /></button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: "thin" }}
        >
          {filtered.map((p) => (
            <article
              key={p.id}
              data-testid={NARRATIVE.projectItem(p.id)}
              className="snap-start shrink-0 w-[85%] sm:w-[420px] border border-white/15 rounded-md overflow-hidden bg-white/[0.03] group hover:border-emerald-400/50 transition-colors"
            >
              <div className="aspect-[16/10] overflow-hidden relative">
                <img
                  src={p.image_url}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  width="800" height="500" decoding="async" loading="lazy"
                />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-medium ${p.tipo === "azienda" ? "bg-emerald-800 text-white" : "bg-emerald-800 text-white"}`}>
                    {p.tipo}
                  </span>
                  {p.kwp && (
                    <span className="px-2 py-1 rounded text-[10px] uppercase tracking-wider bg-black/60 backdrop-blur text-white flex items-center gap-1">
                      <Zap size={10} /> {p.kwp} kWp
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display font-bold text-lg leading-tight mb-2 text-white">{p.title}</h3>
                <div className="flex items-center gap-3 text-xs text-white/75 mb-3">
                  <span className="flex items-center gap-1"><MapPin size={11} /> Scenario · {p.location}</span>
                  <span className="flex items-center gap-1"><Calendar size={11} /> Esempio</span>
                </div>
                {p.description && (
                  <p className="text-sm text-white/70 leading-relaxed">{p.description}</p>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
